import { useCallback, type MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { graphql, useFragment, useMutation } from 'react-relay';
import { LABEL_VALUE_MONITORING_GROUP_CART_ACTION } from 'dibs-checkout/src/constants/multiItemCheckoutMonitoringGroups.ts';
import {
    setMultiCartItem,
    setCartRefetchTrigger,
    type CartAddedNotificationPayload,
} from '../actions/cartActions.ts';

import { type useAddItemToCart_item$key } from './__generated__/useAddItemToCart_item.graphql.ts';
import { type useAddItemToCart_userCartItemAddMutation } from './__generated__/useAddItemToCart_userCartItemAddMutation.graphql.ts';

const itemFragment = graphql`
    fragment useAddItemToCart_item on Item {
        serviceId
        defaultSkuId @required(action: LOG)
    }
`;

export const userCartItemAddMutation = graphql`
    mutation useAddItemToCart_userCartItemAddMutation(
        $input: UserCartItemAddOrUpdateInput!
        $monitoringGroupName: String!
    ) @dibsMonitoringOperation(groupName: $monitoringGroupName) {
        userCartItemAddOrUpdate(input: $input) {
            cartItem {
                serviceId
                skuId
            }
        }
    }
`;

export const useAddItemToCart = ({
    userId,
    item: itemRef,
    skuId,
    quantitySelected,
    onClick,
    onAddedToCart,
}: {
    userId: string | null;
    item: useAddItemToCart_item$key | null;
    skuId: string | null;
    quantitySelected: string;
    onClick?: (event?: MouseEvent<HTMLElement>) => void;
    onAddedToCart?: (payload: CartAddedNotificationPayload) => void;
}): {
    addItemToCart: ({
        type,
        event,
    }: {
        type?: 'CHECKOUT' | 'SAVE_FOR_LATER';
        event?: MouseEvent<HTMLElement>;
    }) => Promise<void>;
    isAddingItemToCart: boolean;
} => {
    const dispatch = useDispatch();
    const item = useFragment(itemFragment, itemRef);
    const { serviceId: itemId, defaultSkuId } = item || {};

    const [commitUserCartItemAdd, userCartItemAddInFlight] =
        useMutation<useAddItemToCart_userCartItemAddMutation>(userCartItemAddMutation);
    const addItemToCart = useCallback(
        async ({
            type = 'CHECKOUT',
            event,
        }: {
            type?: 'CHECKOUT' | 'SAVE_FOR_LATER';
            event?: MouseEvent<HTMLElement>;
        }): Promise<void> => {
            const sharedParams = {
                skuId: skuId || defaultSkuId || '',
                quantity: parseInt(quantitySelected, 10) || 1,
                type,
            } as const;
            const skuIdParam = skuId || defaultSkuId;
            if (userId) {
                try {
                    await new Promise<void>((resolve, reject) => {
                        commitUserCartItemAdd({
                            variables: {
                                input: {
                                    buyerId: parseInt(userId, 10),
                                    itemId: itemId || '',
                                    ...sharedParams,
                                },
                                monitoringGroupName: LABEL_VALUE_MONITORING_GROUP_CART_ACTION,
                            },
                            onCompleted: (response, errors) => {
                                if (errors?.length) {
                                    reject(errors[0]);
                                    return;
                                }
                                const cartItem = response.userCartItemAddOrUpdate?.cartItem;
                                const cartItemId = cartItem?.serviceId
                                    ? parseInt(String(cartItem.serviceId), 10)
                                    : undefined;
                                dispatch(setCartRefetchTrigger());
                                onClick?.(event);
                                onAddedToCart?.({
                                    cartItemId: cartItemId ?? null,
                                    itemId: itemId || null,
                                    skuId: cartItem?.skuId || skuIdParam || null,
                                });
                                resolve();
                            },
                            onError: error => {
                                reject(error);
                            },
                        });
                    });
                } catch {
                    return;
                }
            } else if (!userId && itemId && skuIdParam) {
                dispatch(
                    setMultiCartItem({
                        id: itemId,
                        ...sharedParams,
                        skuId: skuIdParam,
                    })
                );
                onClick?.(event);
                onAddedToCart?.({
                    cartItemId: null,
                    itemId,
                    skuId: skuIdParam,
                });
            }
        },
        [
            userId,
            itemId,
            skuId,
            quantitySelected,
            onClick,
            onAddedToCart,
            commitUserCartItemAdd,
            defaultSkuId,
            dispatch,
        ]
    );

    return {
        addItemToCart,
        isAddingItemToCart: userCartItemAddInFlight,
    };
};
